import React from 'react';
import {StyleSheet, View, Image, Platform} from 'react-native';
import AVTouchableOpacity from '../AVTouchableOpacity';
import AVText from '../AVText';
import AVFormattedCurrency from '../AVFormattedCurrency';
import Styles from '../../Styles';
import {ensureHttps} from 'src/services/StringUtils';
import AccountStore from 'src/stores/AccountStore';
import {MenuProductType} from 'src/types/Menu';
import {DNA} from 'src/types/dnaTypes';
import {
  getCalorieIndicator,
  getOverrideCalorieIndicator,
} from './healthGradeHelper';
import {getDescriber} from './descriptor/DescriptorType';
import Localized from 'src/constants/AppStrings';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

const {getMenuListCompnentsFontFamily} = getDescriber();
const {shadowStyles, bodyFontSize, bodyLargeFontSize} =
  getLayoutDescriber();

type TransactionUpsellItemProps = {
  item: MenuProductType;
  onPress: () => void;
  dna?: DNA;
};

const TransactionUpsellItem: React.FC<TransactionUpsellItemProps> = ({
  item,
  onPress,
  dna,
}) => {
  const displayHealthGrade = dna?.showColorCodes && !dna?.isDisabled;
  const calorieUnit = dna?.showCaloriesAsKilocalories
    ? Localized.Labels.kcal
    : Localized.Labels.cal_not_all_caps;
  const caloriesLabel = item?.calories;
  const parsedCalorie =
    typeof caloriesLabel === 'number'
      ? caloriesLabel
      : parseInt('' + caloriesLabel);

  const {IconComponent, label, hint} = getCalorieIndicator(parsedCalorie, dna);
  const overrideDna = item.overrideDna;
  const hasOverride = overrideDna && overrideDna !== 'Nope';
  const overrideResult = hasOverride
    ? getOverrideCalorieIndicator(overrideDna)
    : null;

  const OverrideIcon = overrideResult?.overrideComponent;
  const showCalories =
    displayHealthGrade && !isNaN(parsedCalorie) && parsedCalorie >= 0;
  const showReardsPoints = item?.RewardPoints > 0;
  return (
    <View
      style={styles.container}
      accessible
      testID="qa_Checkout.UpsellItem_txt"
      nativeID="qa_Checkout.UpsellItem_txt"
    >
      <View style={styles.labelView}>
        <AVText
          style={styles.productNameText}
          numberOfLines={1}
          accessibilityLabel={`${item.name} Text`}
          accessible
          testID="qa_Checkout.UpsellName_txt"
          nativeID="qa_Checkout.UpsellName_txt"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
        >
          {item.name}
        </AVText>

        <View style={styles.calView}>
          {dna?.showCalories && caloriesLabel && (
            <AVText
              style={styles.caloriesLabel}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm13}
            >
              {`${caloriesLabel} ${calorieUnit}`}
            </AVText>
          )}
          <View style={styles.calContainerView}>
            {showCalories && (
              <View
                accessibilityLabel={`${Localized.Labels.health_grade}: ${label}`}
                accessibilityHint={hint}
                style={styles.healthGrade}
              >
                {hasOverride ? (
                  <OverrideIcon
                    width={Styles.Spacing.m3 + 2}
                    height={Styles.Spacing.m3 + 2}
                  />
                ) : (
                  <IconComponent
                    width={Styles.Spacing.m3 + 2}
                    height={Styles.Spacing.m3 + 2}
                  />
                )}
              </View>
            )}

            {showReardsPoints && (
              <View style={styles.pointsView}>
                {showCalories && showReardsPoints && (
                  <View style={styles.blackDotSymbol} />
                )}
                <AVText
                  style={styles.transPoints}
                  maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm13}
                >
                  {item.RewardPoints} {Localized.Labels.points.toLowerCase()}
                </AVText>
              </View>
            )}
          </View>
        </View>

        <AVFormattedCurrency
          value={item.price}
          style={styles.priceText}
          currency={AccountStore.getCurrency()}
          accessible
          testID="qa_Checkout.UpsellPrice_txt"
          nativeID="qa_Checkout.UpsellPrice_txt"
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
        />
      </View>
      {item.smallimageurl && (
        <Image
          source={{uri: ensureHttps(item.smallimageurl)}}
          style={styles.imageView}
          accessibilityLabel={`${item.name} Image`}
        />
      )}
      <AVTouchableOpacity
        onPress={onPress}
        hitSlop={{top: 30, bottom: 30, left: 30, right: 30}}
        accessible
        accessibilityLabel="upsell_item"
        testID="qa_Checkout.AddUpsell_btn"
      >
        <View
          style={[
            styles.circleStyle,
            !item?.smallimageurl && styles.circleViewBorder,
          ]}
          accessible
          testID="qa_Checkout.AddUpsell_btn"
          nativeID="qa_Checkout.AddUpsell_btn"
        >
          <AVText style={styles.plusLabel}>+</AVText>
        </View>
      </AVTouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m2,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase11,
    justifyContent: 'space-between',
    flexDirection: 'row',
    height: 94,
    marginRight: Styles.Spacing.m2,
    width:
      Platform.OS === 'web'
        ? Styles.ScreenWidth / 2
        : Styles.ScreenWidth - (Styles.Spacing.m3 + Styles.Spacing.m2) * 2,
    marginBottom: Styles.Spacing.m1,
    ...shadowStyles().lightShadow,
  },
  labelView: {
    flex: 1,
    justifyContent: 'space-between',
    marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
    marginTop: Styles.Spacing.m1 + Styles.Spacing.m2,
    marginBottom: Styles.Spacing.m2 + 2,
  },
  productNameText: {
    color: Styles.Colors.PayNew.black01,
    fontWeight: '700',
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: bodyFontSize(),
  },
  calView: {
    flexDirection: 'row',
    marginVertical: Styles.Spacing.m1 - 2,
    width: '80%',
  },
  caloriesLabel: {
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: bodyFontSize(),
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase03,
  },
  healthGrade: {
    marginTop: Styles.Spacing.m2 - 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -1,
  },
  priceText: {
    fontFamily: getMenuListCompnentsFontFamily(),
    fontSize: bodyLargeFontSize(),
    fontWeight: '700',
    color: Styles.Colors.PayNew.black01,
    width: '40%',
  },
  imageView: {
    width: 117,
    height: 93,
    borderBottomRightRadius: Styles.Spacing.m2,
    borderTopRightRadius: Styles.Spacing.m2,
  },
  circleStyle: {
    backgroundColor: Styles.Colors.PayNew.white01,
    borderRadius: Styles.Spacing.m4,
    width: Styles.Spacing.m4,
    height: Styles.Spacing.m4,
    justifyContent: 'center',
    alignItems: 'center',
    bottom: Styles.Spacing.m2 + 1,
    right: Styles.Spacing.m2 + 1,
    position: 'absolute',
  },
  plusLabel: {
    fontFamily: Styles.FontFamily.robotoRegular,
    fontSize: bodyFontSize(),
    color: Styles.black,
    fontWeight: '700',
  },
  transPoints: {
    fontSize: bodyFontSize(),
    fontWeight: '400',
    color: Styles.Colors.PayNew.darkHuesBase03,
    fontFamily: getMenuListCompnentsFontFamily(),
  },
  pointsView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  blackDotSymbol: {
    width: Styles.Spacing.m2,
    height: Styles.Spacing.m2,
    borderRadius: Styles.Spacing.m1,
    backgroundColor: Styles.black,
    marginHorizontal: Styles.Spacing.m2,
  },
  calContainerView: {
    flexDirection: 'row',
  },
  circleViewBorder: {
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
  },
});

export default TransactionUpsellItem;
